// VideoUploader.jsx
import React, { useEffect, useId, useRef, useState } from "react";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebaseConfig";
import { PITCH_VIDEO_FOLDER } from "../../utils/safeVideoUrl";

// Pitch videos upload straight from the browser into our Storage bucket. The
// public, unauthenticated upload is bounded by storage.rules (pitch-videos:
// video only, same size cap, create-only), so keep this limit in step with
// it. 500MB fits a minute or more of phone video at any common setting, 4K
// included, so applicants never have to compress their pitch.
const MAX_VIDEO_MB = 500;
const MAX_VIDEO_BYTES = MAX_VIDEO_MB * 1024 * 1024;

// The folder is readable by exact path but can't be listed, so each video
// gets an unguessable name.
function randomFileId() {
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) =>
    b.toString(16).padStart(2, "0")
  ).join("");
}

const VideoUploader = ({ onVideoUploaded, onUploadingChange }) => {
  const inputId = useId();
  const inputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [failedFile, setFailedFile] = useState(null);
  const [isUploaded, setIsUploaded] = useState(false);
  const taskRef = useRef(null);

  // Closing the form mid-upload abandons the upload.
  useEffect(() => () => taskRef.current?.cancel(), []);

  const uploadFile = async (file) => {
    setIsUploading(true);
    onUploadingChange(true);
    setIsUploaded(false);
    setUploadProgress(0);
    setUploadError(null);
    setFailedFile(null);

    try {
      const ext = /\.([a-z0-9]{1,5})$/i.exec(file.name)?.[1].toLowerCase();
      const storageRef = ref(
        storage,
        `${PITCH_VIDEO_FOLDER}/${randomFileId()}${ext ? `.${ext}` : ""}`
      );
      const task = uploadBytesResumable(storageRef, file, { contentType: file.type });
      taskRef.current = task;
      task.on("state_changed", (snapshot) => {
        setUploadProgress(Math.round((snapshot.bytesTransferred * 100) / snapshot.totalBytes));
      });
      await task;

      onVideoUploaded(await getDownloadURL(storageRef));
      setUploadProgress(100);
      setIsUploaded(true);
    } catch (error) {
      if (error?.code === "storage/canceled") return;
      console.error("Pitch video upload failed:", error);
      setUploadError("The upload didn't go through. Try again, or paste a link to your video below.");
      setFailedFile(file);
    } finally {
      taskRef.current = null;
      setIsUploading(false);
      onUploadingChange(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    // A new pick replaces whatever video was attached before, even if the
    // pick turns out to be invalid or its upload fails.
    onVideoUploaded("");
    setIsUploaded(false);

    if (!file.type.startsWith("video/") || file.size === 0) {
      setFailedFile(null);
      setUploadError("That file isn't a video. Choose a video file, or paste a link to your video below.");
      return;
    }

    if (file.size > MAX_VIDEO_BYTES) {
      setFailedFile(null);
      setUploadError(`That video is over ${MAX_VIDEO_MB}MB. Trim or compress it, or paste a link to your video below.`);
      return;
    }

    uploadFile(file);
  };

  // Lets an applicant on a stalled connection give up and paste a link.
  const handleCancel = () => {
    taskRef.current?.cancel();
    inputRef.current.value = "";
  };

  return (
    <div style={{ marginBottom: "16px" }}>
      <label htmlFor={inputId} style={{ display: "block", fontWeight: "bold", marginBottom: '4px' }}>
        Upload a 60-second pitch video *
      </label>
      <small style={{ display: "block", color: "var(--mb-ink-60)", marginBottom: "4px" }}>
        A pitch video is required to be considered for a micro-grant. Upload it here (max {MAX_VIDEO_MB}MB; MP4, MOV or WebM) or paste a link below.
      </small>

      {/* File input */}
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept="video/*"
        onChange={handleFileSelect}
        disabled={isUploading}
        style={{
          width: "100%",
          padding: "8px",
          boxSizing: 'border-box',
          background: 'var(--mb-chalk)',
          color: 'var(--mb-ink)',
          border: '2px solid var(--mb-ink)',
          cursor: isUploading ? 'not-allowed' : 'pointer'
        }}
      />

      {/* Progress bar */}
      {isUploading && (
        <div style={{ marginTop: "10px" }}>
          <div
            role="progressbar"
            aria-label="Video upload progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={uploadProgress}
            style={{ height: "12px", width: "100%", background: "var(--mb-paper)", border: "2px solid var(--mb-ink)", overflow: "hidden" }}
          >
            <div
              style={{
                height: "100%",
                width: `${uploadProgress}%`,
                background: "repeating-linear-gradient(90deg, var(--mb-magenta) 0px, var(--mb-magenta) 8px, var(--mb-butter) 8px, var(--mb-butter) 16px)",
                transition: "width 0.3s ease"
              }}
            />
          </div>
          <small style={{ color: "var(--mb-ink-60)", fontFamily: "var(--font-numeral)", fontSize: 11, display: "block", marginTop: 6 }}>
            {uploadProgress}% uploaded
          </small>
          {/* Phones can pause a background tab, which stalls a long upload. */}
          <small style={{ color: "var(--mb-ink-60)", display: "block", marginTop: 2 }}>
            Keep this page open until the upload finishes.
          </small>
          <button
            onClick={handleCancel}
            type="button"
            className="mb-btn mb-btn-chalk"
            style={{ padding: "6px 12px", fontSize: 11, marginTop: "8px" }}
          >
            Cancel upload
          </button>
        </div>
      )}

      {/* Error message, with a retry when the upload itself failed */}
      {uploadError && (
        <div role="alert" style={{ marginTop: "10px", padding: "12px", background: "var(--error-bg)", border: "2px solid var(--error-text)", color: "var(--error-text)" }}>
          <div style={{ fontSize: "13px", fontFamily: "var(--font-content)" }}>
            {uploadError}
          </div>
          {failedFile && (
            <button
              onClick={() => uploadFile(failedFile)}
              type="button"
              className="mb-btn mb-btn-chalk"
              style={{ padding: "6px 12px", fontSize: 11, marginTop: "8px" }}
            >
              Try again
            </button>
          )}
        </div>
      )}

      {/* Success message */}
      {isUploaded && !isUploading && !uploadError && (
        <div role="status" style={{
          marginTop: "10px",
          padding: "12px",
          background: "var(--success-bg)",
          border: "2px solid var(--success-text)",
          color: "var(--success-text)",
          fontSize: "13px",
          fontFamily: "var(--font-content)"
        }}>
          Video uploaded successfully. It will be submitted with your application.
        </div>
      )}
    </div>
  );
};

export default VideoUploader;
